import {
  forwardRef,
  useContext,
  MouseEvent,
  useRef,
  ComponentPropsWithoutRef,
  KeyboardEvent,
} from "react";
import { useMergeRefs, useTrackingId } from "../../hooks";
import { motion, useReducedMotion } from "motion/react";
import { core } from "@servicetitan/hammer-token";
import { DataTrackingId } from "../../types";
import { TabContext } from "./internal/TabContext";

import styles from "./Tab.module.scss";
import cx from "classnames";
/**
 * Props for the TabButton component
 * @extends Omit<ComponentPropsWithoutRef<"button">, "onClick" | "disabled">
 */
export type TabButtonProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "onClick" | "disabled"
> & {
  /**
   * Unique identifier for the tab button.
   */
  id: string;
  /**
   * ID of the tab panel that this button controls.
   */
  controls: string;
  /**
   * Callback function called when the tab button is clicked.
   * @param e The mouse event
   * @param index The index of the tab button
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>, index?: number) => void;
} & DataTrackingId;

/**
 * TabButton component for individual tab navigation buttons.
 *
 * Features:
 * - Automatic ARIA attributes for accessibility
 * - Visual indicator for active state
 * - Keyboard navigation support
 * - Smooth animations with reduced motion support
 * - Proper focus management
 * - RTL language support
 *
 * @example
 * <Tab.Button id="tab1" controls="panel1" onClick={(e, index) => console.log('Tab clicked:', index)}>
 *   First Tab
 * </Tab.Button>
 */
export const TabButton = forwardRef<HTMLButtonElement, TabButtonProps>(
  (props, ref) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const shouldReduceMotion = useReducedMotion();
    const {
      current,
      items,
      setCurrent,
      focus,
      setFocus,
      onKeyDownHandler: focusController,
      uid,
    } = useContext(TabContext);

    const {
      onClick,
      children,
      onFocus,
      onKeyDown,
      controls,
      id,
      className,
      style,
      ...rest
    } = props;

    const index =
      items && Array.prototype.indexOf.call(items, buttonRef.current);

    const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      if (index === undefined) return;
      onClick?.(e, index);

      setCurrent?.(index);

      setFocus?.(index);
      items?.[index].focus();
    };

    const onKeyDownHandler = (e: KeyboardEvent<HTMLButtonElement>) => {
      focusController?.(e);
      onKeyDown?.(e);
    };

    const data = {
      id,
      controls,
    };

    const trackingId = useTrackingId({
      name: "TabButton",
      data,
      hasOverride: !!rest["data-tracking-id"],
    });

    const tabButtonClassNames = cx(styles["tab-button"], className);

    return (
      <button
        data-tracking-id={trackingId}
        type="button"
        role="tab"
        data-anv="tab-button"
        id={id}
        className={tabButtonClassNames}
        aria-selected={items?.[current].id === id}
        aria-controls={controls}
        onClick={onClickHandler}
        onKeyDown={onKeyDownHandler}
        tabIndex={items?.[focus].id === id ? 0 : -1}
        {...rest}
        ref={useMergeRefs([buttonRef, ref])}
      >
        <span className={styles["inner"]}>{children}</span>
        {items?.[current].id === id && (
          <motion.span
            layoutId={uid}
            className={styles["indicator"]}
            transition={{
              duration: shouldReduceMotion
                ? 0
                : +core.primitive.Duration.value.replace("ms", "") / 1000,
            }}
          />
        )}
      </button>
    );
  },
);

TabButton.displayName = "TabButton";
